import { useEffect, useRef, useState } from "react";
import { cn } from "@/shared/lib/utils";
import { classColorStyle } from "@/shared/lib/wowClass";
import type { PresenceMember, RemoteCursor } from "../api/splits.realtime";
import type { BoardCharacter } from "../splits.types";
import { CharacterCard } from "./CharacterCard";

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

// Absolutely-positioned, non-interactive overlay of remote officers' cursors and drag ghosts.
// Coordinates arrive as board-relative fractions (0..1); we measure this layer (inset-0 over the
// board container) and place each cursor via a transform so the 50ms transition smooths the gaps
// between the ~20/s frames. A cursor that wanders past the board edge clamps to it.
export function CursorLayer({
  cursors,
  members,
  charactersById,
}: {
  cursors: Map<string, RemoteCursor>;
  members: Map<string, PresenceMember>;
  charactersById: Map<string, BoardCharacter>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    measure();
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {[...cursors].map(([id, cursor]) => {
        const member = members.get(id);
        if (!member) return null;
        const left = clamp(cursor.x, 0, 1) * size.w;
        const top = clamp(cursor.y, 0, 1) * size.h;
        const color = classColorStyle(member.mainClass);
        const ghost = cursor.draggedCharacterId
          ? charactersById.get(cursor.draggedCharacterId)
          : undefined;
        return (
          <div
            key={id}
            className="absolute left-0 top-0 transition-transform duration-[50ms] ease-linear"
            style={{ transform: `translate3d(${left}px, ${top}px, 0)` }}
          >
            <svg
              viewBox="0 0 16 16"
              className={cn(
                "size-4 drop-shadow",
                !color && "text-muted-foreground",
              )}
              style={color}
              role="img"
              aria-label={`${member.label} cursor`}
              fill="currentColor"
            >
              <title>{`${member.label} cursor`}</title>
              <path d="M1 1l5.5 13 2.2-5.3L14 6.5 1 1z" />
            </svg>
            <span
              className={cn(
                "-mt-1 ml-3 inline-block align-top whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium",
                "bg-background/90",
                !color && "text-muted-foreground",
              )}
              style={color}
            >
              {member.label}
            </span>
            {ghost && (
              <div
                className="-mt-1 w-52 rounded-md border-2 opacity-70"
                style={{ borderColor: color?.color }}
              >
                <CharacterCard character={ghost} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
